import { scaleBand, scaleLinear } from 'd3-scale'
import { axisTop, axisLeft } from 'd3-axis'
import { select } from 'd3-selection'
import 'd3-transition'
import { showSVGTooltip, emptyElement } from '../utils'

import { calculatePanel2Height, barHeight, chart2Width, TEXT_FONT_SIZE } from './schoolAPContext'

export function schoolAPStats(apData) {
  const container = document.getElementById('apChart2')
  emptyElement(container)

  const subjects = apData.map((item) => item.subject)
  const panel2Height = calculatePanel2Height(subjects)
  const MARGIN = { top: 60, right: 20, bottom: 40, left: 0 }
  const LEGEND_ITEM_SPACING = 60
  const CIRCLE_RADIUS = 8
  const LEGEND_COLOR = '#181818'

  const width = chart2Width - MARGIN.left - MARGIN.right
  const height = panel2Height - MARGIN.top - MARGIN.bottom + 5

  const nStudents = apData.map((item) => item.nStudents)

  const maxStudents = Math.round(Math.max(...nStudents) / 10 + 1) * 10

  const xScale = scaleLinear()
    .domain([0, maxStudents])
    .range([MARGIN.left, width - MARGIN.right])

  const totalHeight = subjects.length * barHeight
  const yScale = scaleBand().domain(subjects).range([0, totalHeight]).padding(0.1)

  const svg = select('#apChart2')
    .append('svg')
    .attr(
      'viewBox',
      `0 0 ${width + MARGIN.left + MARGIN.right} ${height + MARGIN.top + MARGIN.bottom}`
    )
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .classed('svg-content-responsive', true)
    .append('g')
    .attr('transform', `translate(0, ${MARGIN.top})`)

  const legend = svg.append('g').attr('transform', 'translate(30, -45)')
  const legendData = [{ color: LEGEND_COLOR, label: '# of Students taking AP' }]

  legend
    .selectAll('g')
    .attr('class', 'legend')
    .data(legendData)
    .enter()
    .append('g')
    .attr('transform', (d, i) => `translate(${i * LEGEND_ITEM_SPACING}, 0)`)
    .each(function (d) {
      select(this)
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 10)
        .attr('r', CIRCLE_RADIUS)
        .attr('fill', d.color)

      select(this)
        .append('text')
        .attr('x', 15)
        .attr('y', 15)
        .attr('text-anchor', 'start')
        .style('font-size', TEXT_FONT_SIZE)
        .text(d.label)
    })

  svg
    .append('g')
    .attr('class', 'xAxis')
    .call(axisTop(xScale).ticks(5))
    .call((g) => {
      g.selectAll('text').style('font-size', TEXT_FONT_SIZE)
    })
    .call((g) => {
      g.selectAll('.domain').remove()
      g.selectAll('.tick text')
        .attr('text-anchor', (d, i) => (i === 0 ? 'end' : 'middle'))
        .attr('transform', (d, i) => (i === 0 ? 'translate(10,0)' : 'translate(0,0)'))
      g.append('line')
        .attr('x1', xScale.range()[0])
        .attr('x2', xScale.range()[1] + 10)
        .attr('y1', yScale.range()[0])
        .attr('y2', yScale.range()[0])
        .attr('stroke', '#D3D3D3')
      g.append('line')
        .attr('x1', xScale.range()[0] - MARGIN.left)
        .attr('x2', xScale.range()[1] + 10)
        .attr('y1', yScale.range()[1])
        .attr('y2', yScale.range()[1])
        .attr('stroke', '#D3D3D3')
    })

  svg
    .append('g')
    .attr('class', 'yAxis')
    .call(axisLeft(yScale).ticks(0))
    .call((g) => {
      g.selectAll('.domain').remove()
      g.append('line')
        .attr('x1', xScale.range()[0])
        .attr('x2', xScale.range()[0])
        .attr('y1', yScale.range()[0] - 50)
        .attr('y2', yScale.range()[1] + 20)
        .attr('stroke', '#D3D3D3')
      g.append('line')
        .attr('x1', xScale.range()[1] + 10)
        .attr('x2', xScale.range()[1] + 10)
        .attr('y1', yScale.range()[0] - 50)
        .attr('y2', yScale.range()[1] + 20)
        .attr('stroke', '#D3D3D3')
    })

  const tooltip = select('body').append('div').attr('class', 'cb-tooltip')

  svg
    .append('g')
    .selectAll('rect')
    .data(apData)
    .enter()
    .append('rect')
    .attr('x', 0)
    .attr('y', (d) => yScale(d.subject))
    .attr('width', (d) => xScale(d.nStudents))
    .attr('height', barHeight - 1)
    .attr('fill', LEGEND_COLOR)
    .on('pointerenter', (event, d) => {
      showSVGTooltip(tooltip, {
        'AP Exam': d.subject,
        '# of Students': d.nStudents,
        '% of Students': d.pStudents,
        'Avg AP Score': d.avgScore,
      })
    })
    .on('pointermove', (event) => {
      tooltip.style('top', event.pageY - 10 + 'px').style('left', event.pageX + 10 + 'px')
    })
    .on('pointerleave', () => {
      tooltip.style('visibility', 'hidden')
    })

  svg
    .append('g')
    .selectAll('.label')
    .data(apData)
    .enter()
    .append('text')
    .attr('class', 'label')
    .attr('x', (d) => Math.min(xScale(d.nStudents) + 5, width + 5))
    .attr('y', (d) => yScale(d.subject) + yScale.bandwidth() / 2)
    .attr('dy', '.35em')
    .style('font-size', TEXT_FONT_SIZE)
    .text((d) => d.nStudents)
}

export function schoolAPStatsPercentage(apData) {
  const container = document.getElementById('apChart2')
  emptyElement(container)

  const subjects = apData.map((item) => item.subject)
  const panel2Height = calculatePanel2Height(subjects)
  const MARGIN = { top: 60, right: 20, bottom: 40, left: 0 }
  const LEGEND_ITEM_SPACING = 60
  const CIRCLE_RADIUS = 8
  const LEGEND_COLOR = '#181818'

  const width = chart2Width - MARGIN.left - MARGIN.right
  const height = panel2Height - MARGIN.top - MARGIN.bottom + 5

  const pStudents = apData.map((item) => item.pStudents)

  const maxStudents = Math.round(Math.max(...pStudents) / 5 + 1) * 5

  const xScale = scaleLinear()
    .domain([0, maxStudents])
    .range([MARGIN.left, width - MARGIN.right])

  const totalHeight = subjects.length * barHeight
  const yScale = scaleBand().domain(subjects).range([0, totalHeight]).padding(0.1)

  const svg = select('#apChart2')
    .append('svg')
    .attr(
      'viewBox',
      `0 0 ${width + MARGIN.left + MARGIN.right} ${height + MARGIN.top + MARGIN.bottom}`
    )
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .classed('svg-content-responsive', true)
    .append('g')
    .attr('transform', `translate(0, ${MARGIN.top})`)

  const legend = svg.append('g').attr('transform', 'translate(30, -45)')
  const legendData = [{ color: LEGEND_COLOR, label: '% of Students taking AP' }]

  legend
    .selectAll('g')
    .attr('class', 'legend')
    .data(legendData)
    .enter()
    .append('g')
    .attr('transform', (d, i) => `translate(${i * LEGEND_ITEM_SPACING}, 0)`)
    .each(function (d) {
      select(this)
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 10)
        .attr('r', CIRCLE_RADIUS)
        .attr('fill', d.color)

      select(this)
        .append('text')
        .attr('x', 15)
        .attr('y', 15)
        .attr('text-anchor', 'start')
        .style('font-size', TEXT_FONT_SIZE)
        .text(d.label)
    })

  svg
    .append('g')
    .attr('class', 'xAxis')
    .call(
      axisTop(xScale)
        .ticks(5)
        .tickFormat((d) => `${d}%`)
    )
    .call((g) => {
      g.selectAll('text').style('font-size', TEXT_FONT_SIZE)
    })
    .call((g) => {
      g.selectAll('.domain').remove()
      g.selectAll('.tick text')
        .attr('text-anchor', (d, i) => (i === 0 ? 'end' : 'middle'))
        .attr('transform', (d, i) => (i === 0 ? 'translate(20,0)' : 'translate(0,0)'))
      g.append('line')
        .attr('x1', xScale.range()[0])
        .attr('x2', xScale.range()[1] + 15)
        .attr('y1', yScale.range()[0])
        .attr('y2', yScale.range()[0])
        .attr('stroke', '#D3D3D3')
      g.append('line')
        .attr('x1', xScale.range()[0] - MARGIN.left)
        .attr('x2', xScale.range()[1] + 15)
        .attr('y1', yScale.range()[1])
        .attr('y2', yScale.range()[1])
        .attr('stroke', '#D3D3D3')
    })

  svg
    .append('g')
    .attr('class', 'yAxis')
    .call(axisLeft(yScale).ticks(0))
    .call((g) => {
      g.selectAll('.domain').remove()
      g.append('line')
        .attr('x1', xScale.range()[0])
        .attr('x2', xScale.range()[0])
        .attr('y1', yScale.range()[0] - 50)
        .attr('y2', yScale.range()[1] + 20)
        .attr('stroke', '#D3D3D3')
      g.append('line')
        .attr('x1', xScale.range()[1] + 15)
        .attr('x2', xScale.range()[1] + 15)
        .attr('y1', yScale.range()[0] - 50)
        .attr('y2', yScale.range()[1] + 20)
        .attr('stroke', '#D3D3D3')
    })

  const tooltip = select('body').append('div').attr('class', 'cb-tooltip')

  svg
    .append('g')
    .selectAll('rect')
    .data(apData)
    .enter()
    .append('rect')
    .attr('x', 0)
    .attr('y', (d) => yScale(d.subject))
    .attr('width', (d) => xScale(d.pStudents))
    .attr('height', barHeight - 1)
    .attr('fill', LEGEND_COLOR)
    .on('pointerenter', (event, d) => {
      showSVGTooltip(tooltip, {
        'AP Exam': d.subject,
        '# of Students': d.nStudents,
        '% of Students': d.pStudents,
        'Avg AP Score': d.avgScore,
      })
    })
    .on('pointermove', (event) => {
      tooltip.style('top', event.pageY - 10 + 'px').style('left', event.pageX + 10 + 'px')
    })
    .on('pointerleave', () => {
      tooltip.style('visibility', 'hidden')
    })

  svg
    .append('g')
    .selectAll('.label')
    .data(apData)
    .enter()
    .append('text')
    .attr('class', 'label')
    .attr('x', (d) => Math.min(xScale(d.pStudents) + 2, width + 5))
    .attr('y', (d) => yScale(d.subject) + yScale.bandwidth() / 2)
    .attr('dy', '.35em')
    .style('font-size', TEXT_FONT_SIZE)
    .text((d) => d.pStudents + '%')
}
